import {
  GENDER_FEMALE_COLOR,
  GENDER_MALE_COLOR,
} from '../../Analytics/CrowdCountSection/constants';

const BINS = 100;

const ATTR_KEYS = {
  Smile: 'smile',
  Eyeglasses: 'eyeglasses',
  Sunglasses: 'sunglasses',
  Beard: 'beard',
  Mustache: 'mustache',
  EyesOpen: 'eyesOpen',
  MouthOpen: 'mouthOpen',
  FaceOccluded: 'faceOccluded',
};

const ATTR_LABELS = {
  Smile: 'Smile',
  Eyeglasses: 'Eyeglasses',
  Sunglasses: 'Sunglasses',
  Beard: 'Beard',
  Mustache: 'Mustache',
  EyesOpen: 'Eyes open',
  MouthOpen: 'Mouth open',
  FaceOccluded: 'Face occluded',
};

const ATTR_COLOR = '#4E9BF6';

const EMOTION_COLORS = {
  HAPPY: '#E8A317',
  CALM: '#3D9B8F',
  SURPRISED: '#E07A5F',
  SAD: '#5B8DB8',
  ANGRY: '#D64545',
  FEAR: '#6E7F91',
  CONFUSED: '#C4A35A',
  DISGUSTED: '#7A9E7E',
  UNKNOWN: '#A8ADB6',
};

const emotionColor = (name) => EMOTION_COLORS[String(name || '').toUpperCase()] || EMOTION_COLORS.UNKNOWN;

const titleCase = (value) => String(value || 'Unknown')
  .toLowerCase()
  .replace(/\b\w/g, (c) => c.toUpperCase());

const formatPct = (n, total) => {
  if (!total) return '0%';
  return `${Math.round((n / total) * 100)}%`;
};

const collectBoxes = (tileBoxes) => Object.values(tileBoxes || {}).flat();

const countMapEntries = (map, sortFn) => {
  const entries = [...map.entries()];
  if (sortFn) entries.sort(sortFn);
  else entries.sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0])));
  return entries;
};

const densityFromValues = (values) => {
  const counts = new Array(BINS).fill(0);
  values.forEach((v) => {
    if (!Number.isFinite(v)) return;
    const c = Math.min(99.999, Math.max(0, v));
    counts[Math.min(BINS - 1, Math.floor(c))] += 1;
  });
  return counts;
};


const ageBucketFromRange = (low, high) => {
  const mid = (Number(low) + Number(high)) / 2;
  if (!Number.isFinite(mid)) return null;
  if (mid < 25) return '< 25';
  if (mid < 35) return '25 - 34';
  if (mid < 45) return '35 - 44';
  if (mid < 55) return '45 - 54';
  if (mid < 65) return '55 - 64';
  return '> 65';
};


const parseTileKey = (key) => {
  const [row, col] = String(key).split('-').map(Number);
  return {
    row: Number.isFinite(row) ? row : 0,
    col: Number.isFinite(col) ? col : 0,
  };
};

const joinList = (items) => (items?.length ? items.join(', ') : '—');

const avg = (nums) => {
  if (!nums.length) return null;
  return nums.reduce((s, n) => s + n, 0) / nums.length;
};

/** Boxed label counts + observation metadata (for distribution bars / detail drawer). */
const buildLabelCatalog = (tileBoxes, observations = []) => {
  const map = new Map();

  Object.values(tileBoxes || {}).forEach((boxes) => {
    (boxes || []).forEach((b) => {
      if (!b?.label) return;
      let entry = map.get(b.label);
      if (!entry) {
        entry = {
          name: b.label,
          instanceCount: 0,
          confidences: [],
          parents: [],
          aliases: new Set(),
          categories: new Set(),
        };
        map.set(b.label, entry);
      }
      entry.instanceCount += 1;
      if (Number.isFinite(b.confidence)) entry.confidences.push(b.confidence);
    });
  });

  (observations || []).forEach((obs) => {
    if (!obs?.name) return;
    let entry = map.get(obs.name);
    if (!entry) {
      entry = {
        name: obs.name,
        instanceCount: 0,
        confidences: [],
        parents: [],
        aliases: new Set(),
        categories: new Set(),
      };
      map.set(obs.name, entry);
    }
    if ((obs.parents || []).length) {
      entry.parents = [...obs.parents];
    }
    (obs.aliases || []).forEach((a) => entry.aliases.add(a));
    (obs.categories || []).forEach((c) => entry.categories.add(c));
  });

  return Array.from(map.values())
    .map((entry) => ({
      name: entry.name,
      count: entry.instanceCount,
      instanceCount: entry.instanceCount,
      avgConfidence: avg(entry.confidences),
      minConfidence: entry.confidences.length ? Math.min(...entry.confidences) : null,
      maxConfidence: entry.confidences.length ? Math.max(...entry.confidences) : null,
      parents: entry.parents,
      aliases: Array.from(entry.aliases).sort((a, b) => a.localeCompare(b)),
      categories: Array.from(entry.categories).sort((a, b) => a.localeCompare(b)),
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
};

const labelDetails = (entry) => {
  let confidence = '—';
  if (entry.avgConfidence != null) {
    confidence = `avg ${entry.avgConfidence.toFixed(1)}%`;
    if (entry.minConfidence != null && entry.maxConfidence != null) {
      confidence += ` (${entry.minConfidence.toFixed(1)}–${entry.maxConfidence.toFixed(1)}%)`;
    }
  }
  return [
    ['Count', String(entry.count)],
    ['Confidence', confidence],
    ['Parents', joinList(entry.parents)],
    ['Aliases', joinList(entry.aliases)],
    ['Categories', joinList(entry.categories)],
  ];
};

const LABEL_BOX_COLOR = '#4E9BF6';

const DEMO_COLORS = {
  Age: '#AFBD60',
  Adult: '#F2A86B',
  Teen: '#6CC4C1',
  Child: '#7E8ED9',
  Gender: '#9AA3B2',
  Male: GENDER_MALE_COLOR,
  Man: GENDER_MALE_COLOR,
  Boy: GENDER_MALE_COLOR,
  Female: GENDER_FEMALE_COLOR,
  Woman: GENDER_FEMALE_COLOR,
  Girl: GENDER_FEMALE_COLOR,
};

const pctOf = (part, whole) => (whole ? Math.round((part / whole) * 1000) / 10 : 0);

/**
 * Custom Person Age | Gender trees.
 * Age: Adult / Teen / Child. Gender: Male / Female (+ Man/Woman/Boy/Girl).
 * % = share of parent. Circle size = label count.
 */
const buildPersonDemoTrees = (catalog) => {
  const counts = new Map((catalog || []).map((e) => [e.name, e.count || 0]));
  const n = (name) => counts.get(name) || 0;

  const sizeFor = (count, maxCount) => {
    const minR = 10;
    const maxR = 52;
    const t = Math.sqrt((count || 0) / (maxCount || 1));
    return Math.round(minR + t * (maxR - minR));
  };

  const make = (name, count, parentTotal, maxCount, children) => {
    const pct = pctOf(count, parentTotal);
    return {
      name,
      count,
      pct,
      value: Math.max(count || 0, 1),
      symbolSize: sizeFor(count, maxCount),
      itemStyle: { color: DEMO_COLORS[name] },
      label: { formatter: `${name}  (${count} · ${pct}%)` },
      children: children?.length ? children : undefined,
    };
  };

  const adult = n('Adult');
  const teen = n('Teen');
  const child = n('Child');
  const ageTotal = adult + teen + child;

  const male = n('Male');
  const female = n('Female');
  const genderTotal = male + female;

  const man = n('Man');
  const woman = n('Woman');
  const boy = n('Boy');
  const girl = n('Girl');

  const ageMax = Math.max(adult, teen, child, man, woman, 1);
  const genderMax = Math.max(male, female, man, woman, boy, girl, 1);

  const ageRoot = {
    name: 'Age',
    count: ageTotal,
    pct: 100,
    value: Math.max(ageTotal, 1),
    symbolSize: sizeFor(ageTotal, ageMax),
    itemStyle: { color: DEMO_COLORS.Age },
    label: { formatter: `Age  (${ageTotal})` },
    children: [
      make('Adult', adult, ageTotal, ageMax, [
        make('Man', man, adult, ageMax),
        make('Woman', woman, adult, ageMax),
      ]),
      make('Teen', teen, ageTotal, ageMax),
      make('Child', child, ageTotal, ageMax),
    ],
  };

  const genderRoot = {
    name: 'Gender',
    count: genderTotal,
    pct: 100,
    value: Math.max(genderTotal, 1),
    symbolSize: sizeFor(genderTotal, genderMax),
    itemStyle: { color: DEMO_COLORS.Gender },
    label: { formatter: `Gender  (${genderTotal})` },
    children: [
      make('Male', male, genderTotal, genderMax, [
        make('Man', man, male, genderMax),
        make('Boy', boy, male, genderMax),
      ]),
      make('Female', female, genderTotal, genderMax, [
        make('Woman', woman, female, genderMax),
        make('Girl', girl, female, genderMax),
      ]),
    ],
  };

  return { ageRoot, genderRoot, personCount: n('Person') };
};

const openLabelDetail = (entry, onOpenDetail) => {
  if (!entry) return;
  onOpenDetail?.({
    kind: 'label',
    title: entry.name,
    subtitle: `${entry.count} boxes`,
    rows: labelDetails(entry),
    raw: {
      name: entry.name,
      count: entry.count,
      instanceCount: entry.instanceCount,
      avgConfidence: entry.avgConfidence,
      minConfidence: entry.minConfidence,
      maxConfidence: entry.maxConfidence,
      parents: entry.parents,
      aliases: entry.aliases,
      categories: entry.categories,
    },
  });
};

/** Two Person demographic trees — % always vs parent (children ≈ 100%). */

export {
  BINS,
  ATTR_KEYS,
  ATTR_LABELS,
  ATTR_COLOR,
  emotionColor,
  titleCase,
  formatPct,
  collectBoxes,
  countMapEntries,
  densityFromValues,
  ageBucketFromRange,
  parseTileKey,
  buildLabelCatalog,
  LABEL_BOX_COLOR,
  buildPersonDemoTrees,
  openLabelDetail,
};
